import { createAction, props } from '@ngrx/store';
import { StandingInstruction } from "../../shared/types/StandingInstruction";
import { ViewTypes, transactionTypes } from '../../shared/utilities/Constants';
import { CashieringTransaction, RecurringTransaction } from "../models";
import { ServiceError, ServiceErrorWithList } from '../models/shared/ServiceError';
import { WireAcknowledgeResponseV3 } from '../models/wire/WireAcknowledgeResponseV3';
import { DetailsResponse } from '../models/wire/WireInfo';
import { WireInquiryResponse } from '../models/wire/WireInquiryResponse';
import { DraftCommonWire, WireTemplatesResponse } from '../models/wire/WireTemplatesResponse';
import { WireVerifyResponseV3 } from '../models/wire/WireVerifyResponseV3';
import { WireInputResponseV3 } from '../models/wire/WireInputResponseV3';
import { WireForm } from '../models/form/TransactionForm';

export const callWireDetails = createAction(
  '[Create Transaction - Wire] Call to Wire Details from Async Validator'
);

export const wireDetailsCallSuccessful = createAction(
  '[Create Transaction - Wire] Call to Wire Details succeeded',
  props<DetailsResponse>()
);

export const wireDetailsCallFailed = createAction(
  '[Create Transaction - Wire] Call to Wire Details failed',
  props<ServiceError>()
);

export const wireDetailsCallLoading = createAction(
  '[Create Transaction - Wire] - Show loading spinner for Wire Details'
);


export const callWireInputV3 = createAction(
  /**
  * Action for calling wires input
  * @remarks
  * We pass the standing auth id here instead of in the effect to get around a timing issue when toggling the use standing auth control
  *
  * @param standingAuthId
  * a formatted string of the transaction type, cashiering request id and cashiering second request id
  *
  * Example: w-12343456-2022
  */

  '[Create Transaction - Wire] Call to Wire Input V3',
  props<{ standingAuthId: string; isPalAccountInContext?: boolean }>()
);

export const wireInputCallSuccessfulV3 = createAction(
  '[Create Transaction - Wire] Call to Wire Input V3 succeeded',
  props<{ response: WireInputResponseV3 }>()
);

export const wireInputFailedV3 = createAction(
  '[Create Transaction - Wire] Call to Wire Input V3 failed',
  props<ServiceError>()
);

export const callWireInputV3TemplateFlow = createAction(
  '[Create Transaction - Wire] Call to Wire Input V3 Template Flow',
);

export const wireInputCallV3TemplateFlowSuccessful = createAction(
  '[Create Transaction - Wire] Call to Wire Input V3 succeeded Template Flow',
  props<{ response: WireInputResponseV3 }>()
);

export const wireInputV3TemplateFlowFailed = createAction(
  '[Create Transaction - Wire] Call to Wire Input V3 failed Template Flow',
  props<ServiceError>()
);

export const verifyV3CallSuccessful = createAction(
  '[Create Request - WIRE] Call to verify was successful',
  props<{response: WireVerifyResponseV3, inrReleaseFlowFromTable: boolean}>()
);

export const callWireSloaVerifyV3FromCreateTransactionSuccessful = createAction(
  '[Create Request - WIRE] Call to verify v3 for wire and sloa was successful',
  props<WireVerifyResponseV3>()
);

export const verifyV3CallFailed = createAction(
  '[Create Request - WIRE] Error calling verify v3',
  props<ServiceErrorWithList>()
);

export const callWireSloaVerifyV3FromCreateTransactionFailed = createAction(
  '[Create Request - WIRE] Error calling verify v3 for wire and sloa',
  props<ServiceErrorWithList>()
);

export const callAcknowledgeV3 = createAction(
  '[Create Request - Wire] Call to Wire Acknowledge V3'
);

export const acknowledgeCallV3Successful = createAction(
  '[Create Request - Wire] Call to Wire Acknowledge V3 was successful',
  props<WireAcknowledgeResponseV3>()
);

export const acknowledgeCallV3Fail = createAction(
  '[Create Request - Wire] Call to Wire Acknowledge V3 failed',
  props<ServiceErrorWithList>()
);

export const callInquiry = createAction(
  '[Create Request - Wire] Call to Wire Inquiry',
  props<{ payload: StandingInstruction | CashieringTransaction | RecurringTransaction, viewType: ViewTypes, transactionType: transactionTypes, resourceVersion: string }>()
);

export const inquiryCallSuccessful = createAction(
  '[Create Request - Wire] Call to Wire Inquiry was successful',
  props<{ wireInquiryResponse: WireInquiryResponse }>()
);

export const inquiryCallFailed = createAction(
  '[Create Request - Wire] Call to Wire Inquiry failed',
  props<ServiceError>()
);

export const callWireTemplatesGetList = createAction(
  '[Create Transaction - Wire] Call to get list of Wire templates'
);

export const callWireTemplatesGetListTable = createAction(
  '[Create Transaction - Wire] Call to get list of Wire templates from transaction table'
);

export const callWireTemplatesGetListTableSuccessful = createAction(
  '[Create Transaction - Wire] Call to get list of Wire templates from transaction table succeeded',
  props<WireTemplatesResponse>()
);

export const callWireTemplatesGetListTableFailed = createAction(
  '[Create Transaction - Wire] Call to get list of Wire templates from transaction table failed',
  props<ServiceErrorWithList>()
);

export const callWireTemplatesGetListSuccessful = createAction(
  '[Create Transaction - Wire] Call to get list of Wire templates succeeded',
  props<WireTemplatesResponse>()
);

export const callWireTemplatesGetListFailed = createAction(
  '[Create Transaction - Wire] Call to get list of Wire templates failed',
  props<ServiceErrorWithList>()
);

export const callWiresCreateTemplate = createAction(
  '[Create Transaction - Wire] Call to create a wire template',
  props<{ templateNickname: string }>()
);

export const callWiresCreateTemplateSuccessful = createAction(
  '[Create Transaction - Wire] Call to create a wire template succeeded',
  props<DraftCommonWire>()
);

export const callWiresCreateTemplateFailed = createAction(
  '[Create Transaction - Wire] Call to create a wire template failed',
  props<ServiceErrorWithList>()
);

export const callWiresPatchTemplate = createAction(
  '[Create Transaction - Wire] Call to update a non-ISO wire template',
  props<{ currentTemplate: DraftCommonWire, nickname: string, form: WireForm }>()
);

export const callWiresPatchTemplateSuccessful = createAction(
  '[Create Transaction - Wire] Call to update a non-ISO wire template succeeded',
  props<DraftCommonWire>()
);

export const callWiresPatchTemplateFailed = createAction(
  '[Create Transaction - Wire] Call to update a non-ISO wire template failed',
  props<ServiceErrorWithList>()
);

export const updateWiresTemplateNickname = createAction(
  '[Create Transaction - Wire] Save as Template Modal Nickname updated',
  props<{ nickname: string }>()
);

export const callWireCreateTemplateFromInput = createAction(
  '[Create Transaction - Wire] Call to create a wire template from the input page',
  props<{ templateNickname: string }>()
);

export const callwireDetailsV2Successful = createAction(
  '[Create Transaction - Wire] Call to Wire Details V2 succeeded',
  props<DetailsResponse>()
);

export const callWireDetailsV2 = createAction(
  '[Create Transaction - Wire] Call to Wire Details V2',
  props<{ isPalAccountInContext?: boolean }>()
);
